import Component from "../../lib/component";
import { getThrottledFunction } from "../../helpers/throttle";
import BreakpointComparer from "../breakpointer/breakpoint_comparer";

export const PageHeadingConfig = {
  componentTypeKey: "PageHeading",
  baseHtmlClass: "page_heading",
  selector: ".page_heading",

  titleSelector: ".page_heading__title",
  actionSelector: ".page_heading__action",

  overlayHtmlClass: "page_heading--overlay",

  defaultBreakpoints: { S: [0, "30rem"] },
};

export class PageHeading extends Component {
  static get config() {
    return PageHeadingConfig;
  }

  constructor(id_or_element) {
    super({
      element: id_or_element,
      config: PageHeading.config,
    });

    this.titleElement = this.element.querySelector(
      PageHeading.config.titleSelector,
    );
    this.title = !!this.titleElement ? this.titleElement.innerText : "";

    this.actionElement = this.element.querySelector(
      PageHeading.config.actionSelector,
    );

    // Move all the children (apart from the `actionElement`) into a new container element.
    const children = Array.from(this.element.children);
    const startContainerElement = document.createElement("div");
    startContainerElement.classList.add(
      `${PageHeading.config.baseHtmlClass}__start`,
    );
    this.element.insertBefore(startContainerElement, this.element.firstChild);
    children.forEach((child) => {
      if (child !== this.actionElement)
        startContainerElement.appendChild(child);
    });

    // Move the action element into a new container element.
    if (this.actionElement) {
      const endContainerElement = document.createElement("div");
      endContainerElement.classList.add(
        `${PageHeading.config.baseHtmlClass}__end`,
      );
      this.element.appendChild(endContainerElement);
      endContainerElement.appendChild(this.actionElement);
    }

    this.isPrimary = this.getIsOnPage();
    this.isOverlay = this.getIsOverlay();

    // When the page heading is primary and not overlaid, we need to insert an "offsetter" element to push the page content down.
    if (this.isPrimary && !this.isOverlay) {
      this.offsetterElement = document.createElement("div");
      this.offsetterElement.classList.add(
        `${PageHeading.config.baseHtmlClass}_offsetter`,
      ); // "page_heading_offsetter"
      this.offsetterElement.setAttribute("aria-hidden", "true");
      this.offsetterElement.setAttribute("for-page-heading", this.title);
      document.body.insertBefore(
        this.offsetterElement,
        document.body.firstChild,
      );
    }

    this.breakpoints = this.config.defaultBreakpoints;
    this.width = this.getWidth();
    this.breakpointLabels = this.getBreakpointLabels();
    this.comparer = new BreakpointComparer(this.breakpoints);

    const scrollResizeHandler = getThrottledFunction(
      this.handleScrollResize.bind(this),
      10,
    );
    window.addEventListener("scroll", scrollResizeHandler.bind(this), true);
    window.addEventListener("resize", scrollResizeHandler.bind(this), true);

    this.observer = new ResizeObserver(scrollResizeHandler);
    this.observer.observe(this.element);

    this.render();
  }

  setTitle(title) {
    this.title = title;
  }

  removeActionElement() {
    if (!this.actionElement || !this.actionElement.parentNode) {
      return;
    }
    this.actionElement.remove();
  }

  handleScrollResize(event) {
    this.isScrolled = this.getIsScrolled();
    this.width = this.getWidth();
    this.breakpointLabels = this.getBreakpointLabels();
    this.render();
  }

  getBreakpointLabels() {
    let labels = [];
    const keys = Object.keys(this.breakpoints);
    keys.forEach((key) => {
      if (this.comparer && this.comparer.isWithinBreakpoint(this.width, key))
        labels.push(key);
    });
    return labels;
  }

  getWidth() {
    return this.element.offsetWidth;
  }

  getHeight() {
    return this.element.offsetHeight;
  }

  getIsScrolled() {
    return window.scrollY > this.getHeight();
  }

  // The page heading may be (or may not be) on the documents main #page element.
  // For example it might be inserted into a `Modal` which is outside of the `#page`.
  getIsOnPage() {
    // Is on the "page" if any parent within the body has the id "page".
    let parent = this.element.parentElement;
    while (parent) {
      if (parent.id === "page") return true;
      parent = parent.parentElement;
    }
    return false;
  }

  // The page heading may be overlaid on the page content rather than having a spacer displayed to push content out from under it.
  getIsOverlay() {
    return this.element.classList.contains(PageHeading.config.overlayHtmlClass);
  }

  render() {
    if (this.offsetterElement)
      this.offsetterElement.style.height = `${this.getHeight()}px`;
    this.conditionallySetHtmlClassState(this.isScrolled, "scrolled");
    this.conditionallySetHtmlClassState(this.isPrimary, "primary");
    this.conditionallySetHtmlClassState(this.withLeftNav(), "with_left_nav");
    this.conditionallySetHtmlClassState(this.withRightNav(), "with_right_nav");
    this.renderBreakpointClasses();
  }

  withLeftNav() {
    return !!document.getElementById("nav_global");
  }

  withRightNav() {
    return !!document.getElementById("nav_person");
  }

  renderBreakpointClasses() {
    this.clearAllBreakpointClasses();
    this.addBreakpointClasses();
  }

  clearAllBreakpointClasses() {
    Object.keys(this.breakpoints).forEach((key) => {
      this.element.classList.remove(
        `${PageHeading.config.baseHtmlClass}--${key}`,
      );
    });
  }

  addBreakpointClasses() {
    this.breakpointLabels.forEach((breakpointLabel) => {
      this.element.classList.add(
        `${PageHeading.config.baseHtmlClass}--${breakpointLabel}`,
      );
    });
  }
}
